// frontend/components/POS/Cart.jsx
import React from 'react';
import { TrashIcon, PlusIcon, MinusIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';

export default function Cart({ items = [], onUpdateQuantity, onRemoveItem, onClearCart }) {
  // Safety check - ensure items is an array
  const safeItems = Array.isArray(items) ? items : [];
  
  // Calculate totals
  const subtotalExclTax = safeItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const taxAmount = safeItems.reduce((sum, item) => sum + ((item.price * item.quantity) * (item.tax_rate / 100)), 0);
  const totalInclTax = subtotalExclTax + taxAmount;
  
  // Get effective tax rate (assuming all items have same rate, otherwise calculate weighted average)
  const effectiveTaxRate = subtotalExclTax > 0 ? (taxAmount / subtotalExclTax) * 100 : 16;

  if (safeItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-12">
        <ShoppingBagIcon className="h-16 w-16 text-gray-300 mb-4" />
        <p className="text-gray-500 text-sm sm:text-base">Your cart is empty</p>
        <p className="text-xs sm:text-sm text-gray-400 mt-1">Add products to get started</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Cart Items - Scrollable */}
      <div className="flex-1 overflow-y-auto -mx-2 sm:-mx-4 px-2 sm:px-4">
        {/* Mobile Card View (visible on small screens) */}
        <div className="block md:hidden space-y-3">
          {safeItems.map((item, index) => {
            const itemTotalExclTax = item.price * item.quantity;
            const itemTax = itemTotalExclTax * (item.tax_rate / 100);
            const itemTotalInclTax = itemTotalExclTax + itemTax;
            
            return (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.sku}</p>
                    <p className="text-xs text-blue-600 mt-1">KES {item.price.toLocaleString()} + VAT</p>
                  </div>
                  <button
                    onClick={() => onRemoveItem(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="flex justify-between items-center mt-2">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                      className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                      disabled={item.quantity <= 1}
                    >
                      <MinusIcon className="h-3 w-3" />
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                      className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300"
                    >
                      <PlusIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm">
                      KES {itemTotalInclTax.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      (Inc. VAT)
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop Table View (visible on medium and up) */}
        <div className="hidden md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price (Excl. VAT)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total (Incl. VAT)</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeItems.map((item, index) => {
                const itemTotalExclTax = item.price * item.quantity;
                const itemTax = itemTotalExclTax * (item.tax_rate / 100);
                const itemTotalInclTax = itemTotalExclTax + itemTax;
                
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.sku}</div>
                      <div className="text-[10px] text-gray-400">VAT {item.tax_rate || 16}%</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                          className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50"
                          disabled={item.quantity <= 1}
                        >
                          <MinusIcon className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                          className="p-1 rounded-md hover:bg-gray-100"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      KES {item.price.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      <div>KES {itemTotalInclTax.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-400">Inc. KES {itemTax.toLocaleString()} VAT</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => onRemoveItem(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary - Fixed at bottom */}
      {safeItems.length > 0 && (
        <div className="border-t border-gray-200 pt-4 mt-4 sticky bottom-0 bg-white">
          <div className="space-y-2 px-2 sm:px-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal (Excl. VAT):</span>
              <span>KES {subtotalExclTax.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">VAT ({effectiveTaxRate.toFixed(1)}%):</span>
              <span>KES {taxAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-base sm:text-lg font-bold pt-2 border-t">
              <span>Total (Incl. VAT):</span>
              <span className="text-blue-600">KES {totalInclTax.toLocaleString()}</span>
            </div>
          </div>

          {/* Tax Compliance Note */}
          <div className="mt-2 px-2 sm:px-4">
            <p className="text-[10px] text-gray-400 text-center">
              * VAT included as per KRA requirements. Tax will be shown on invoice.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 px-2 sm:px-4">
            <button
              onClick={onClearCart}
              className="px-3 py-2 sm:py-2.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
            >
              Clear Cart
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('pos-checkout', { 
                detail: { total: totalInclTax, items: safeItems } 
              }))}
              className="px-3 py-2 sm:py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}